"use client";

import { motion } from "motion/react";
import dynamic from "next/dynamic";
import Image from "next/image";
import React from "react";
import IntroHeader from "@/app/intro/components/IntroHeader";
import IntroLogos from "@/app/intro/components/IntroLogos";
import PlanDivider from "@/app/plans/components/PlanDivider";

const BookingCalendar = dynamic(() => import("./components/BookingCalendar"), {
	ssr: false,
	loading: () => (
		<div className="flex h-[400px] w-full items-center justify-center rounded-[20px] border border-[rgba(9,10,12,0.12)]">
			<div className="size-5 animate-spin rounded-full border-2 border-[rgba(9,10,12,0.12)] border-t-[rgba(9,10,12,0.64)]" />
		</div>
	),
});

const UltraWorkModal = dynamic(() => import("./components/UltraWorkModal"), {
	ssr: false,
});

type GsapCore = typeof import("gsap").gsap;

const INITIAL_REACTIONS = [
	{ emoji: "🔥", count: 3, active: false },
	{ emoji: "🙌", count: 2, active: true },
	{ emoji: "👀", count: 1, active: true },
];

function AnimatedSelection() {
	const text = "design and dev";
	const [cursorPos, setCursorPos] = React.useState(0);
	const charRefs = React.useRef<(HTMLSpanElement | null)[]>([]);
	const offsetsRef = React.useRef<number[]>([]);

	// Single effect: measure once, then animate forever
	React.useEffect(() => {
		let cancelled = false;

		const measure = () => {
			if (charRefs.current.length === 0) return false;
			const offsets: number[] = [];
			for (let i = 0; i < charRefs.current.length; i++) {
				const el = charRefs.current[i];
				if (el) offsets.push(el.offsetLeft);
			}
			const last = charRefs.current[charRefs.current.length - 1];
			if (last) offsets.push(last.offsetLeft + last.offsetWidth);
			offsetsRef.current = offsets;
			return offsets.length > 0;
		};

		const humanDelay = () => {
			let d = 60 + Math.random() * 60;
			if (Math.random() < 0.12) d += 120 + Math.random() * 200;
			return d;
		};

		const step = (positions: number[], i: number, onDone: () => void) => {
			if (cancelled) return;
			if (i >= positions.length) {
				onDone();
				return;
			}
			setCursorPos(positions[i]);
			const d =
				text[positions[i]] === " "
					? humanDelay() + 80 + Math.random() * 120
					: humanDelay();
			setTimeout(() => step(positions, i + 1, onDone), d);
		};

		const forward = Array.from({ length: text.length }, (_, i) => i + 1);
		const backward = [...forward].reverse().map((p) => p - 1);

		const loop = () => {
			if (cancelled) return;
			setCursorPos(0);
			setTimeout(() => {
				if (cancelled) return;
				step(forward, 0, () => {
					setTimeout(() => {
						if (cancelled) return;
						step(backward, 0, () => {
							setTimeout(loop, 2000);
						});
					}, 2500);
				});
			}, 800);
		};

		const start = async () => {
			await document.fonts.ready;
			if (cancelled) return;
			if (!measure()) return;
			setTimeout(loop, 600);
		};

		start();
		return () => {
			cancelled = true;
		};
	}, []);

	const cursorLeft = offsetsRef.current[cursorPos] ?? 0;
	const chars = text.split("");

	return (
		<>
			{chars.map((char, i) => (
				<span
					key={i}
					ref={(el) => {
						charRefs.current[i] = el;
					}}
					className={
						i < cursorPos
							? "relative text-[rgba(236,72,153,0.88)]"
							: "relative text-[rgba(9,10,12,0.32)]"
					}
				>
					{char}
				</span>
			))}
			<span
				className="pointer-events-none absolute -inset-y-px rounded-sm bg-[rgba(236,72,153,0.2)] select-none"
				aria-hidden="true"
				style={{
					left: "-2px",
					width: cursorPos > 0 ? cursorLeft + 4 : 0,
				}}
			/>
			<span
				className="pointer-events-none absolute -inset-y-0.5 w-px bg-[#090a0c]"
				aria-hidden="true"
				style={{
					left: `${cursorLeft}px`,
					animation: "cursor-blink 1s step-end infinite",
				}}
			/>
		</>
	);
}

function Reactions() {
	const [reactions, setReactions] = React.useState(INITIAL_REACTIONS);

	const toggle = (index: number) => {
		setReactions((prev) =>
			prev.map((r, i) =>
				i === index
					? {
							...r,
							active: !r.active,
							count: r.active ? r.count - 1 : r.count + 1,
						}
					: r,
			),
		);
	};

	return (
		<div className="flex gap-[3px]">
			{reactions.map((r, i) => (
				<button
					key={r.emoji}
					type="button"
					onClick={() => toggle(i)}
					className={`flex items-center gap-[3px] rounded-full border px-1.5 py-[3px] transition ${
						r.active
							? "border-[#2e639e] bg-[#e7f7fe]"
							: "border-transparent bg-[rgba(9,10,12,0.06)] hover:bg-[rgba(9,10,12,0.1)]"
					}`}
				>
					<span className="text-xs leading-4">{r.emoji}</span>
					<span
						className={`font-display text-xs leading-4 tracking-[0.24px] ${
							r.active ? "text-[#2e639e]" : "text-[rgba(9,10,12,0.88)]"
						}`}
					>
						{r.count}
					</span>
				</button>
			))}
		</div>
	);
}

export default function UltraWaitlistClient() {
	const [isWorkModalOpen, setIsWorkModalOpen] = React.useState(false);

	const headerRef = React.useRef<HTMLDivElement>(null);
	const heroRef = React.useRef<HTMLDivElement>(null);
	const bodyRef = React.useRef<HTMLDivElement>(null);
	const formRef = React.useRef<HTMLDivElement>(null);
	const footerRef = React.useRef<HTMLDivElement>(null);
	const gsapModuleRef = React.useRef<GsapCore | null>(null);
	const timelineRef = React.useRef<ReturnType<GsapCore["timeline"]> | null>(
		null,
	);

	React.useEffect(() => {
		const root = document.documentElement;
		root.dataset.lenisDisabled = "true";
		return () => {
			delete root.dataset.lenisDisabled;
		};
	}, []);

	React.useEffect(() => {
		if (typeof window === "undefined") return;

		let cancelled = false;

		const animate = async () => {
			if (!gsapModuleRef.current) {
				const mod = await import("gsap");
				const instance =
					(mod as unknown as { gsap?: GsapCore }).gsap ??
					(mod as unknown as { default?: GsapCore }).default ??
					(mod as unknown as GsapCore);
				gsapModuleRef.current = instance;
			}

			if (cancelled) return;

			const gsap = gsapModuleRef.current as GsapCore;
			const prefersReducedMotion = window.matchMedia(
				"(prefers-reduced-motion: reduce)",
			).matches;

			if (prefersReducedMotion) return;

			const sections = [
				headerRef.current,
				heroRef.current,
				bodyRef.current,
				formRef.current,
				footerRef.current,
			].filter((node): node is HTMLDivElement => Boolean(node));

			if (!sections.length) return;

			timelineRef.current?.kill();

			gsap.set(sections, {
				opacity: 0,
				y: 24,
				filter: "blur(24px)",
			});

			timelineRef.current = gsap.timeline({
				defaults: { duration: 0.6, ease: "power2.out" },
			});

			timelineRef.current.to(sections, {
				opacity: 1,
				y: 0,
				filter: "blur(0px)",
				stagger: 0.12,
			});
			timelineRef.current.eventCallback("onComplete", () => {
				gsap.set(sections, { clearProps: "transform,filter" });
			});
		};

		void animate();

		return () => {
			cancelled = true;
			timelineRef.current?.kill();
		};
	}, []);

	return (
		<div className="relative flex min-h-screen flex-col overflow-x-clip bg-white">
			{/* Header */}
			<div className="pointer-events-none fixed inset-x-0 top-0 z-40 p-4">
				<div ref={headerRef}>
					<IntroHeader />
				</div>
			</div>

			{/* Top gradient (below nav) */}
			<div className="pointer-events-none fixed inset-x-0 top-0 z-30 h-12 bg-gradient-to-b from-white to-transparent md:h-16" />
			{/* Bottom gradient */}
			<div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 h-12 bg-gradient-to-b from-transparent to-white md:h-16" />

			{/* Page Content */}
			<div className="relative z-10 mx-auto flex w-full max-w-[588px] flex-1 flex-col items-center gap-20 px-6 pb-20 pt-36">
				{/* Hero */}
				<div ref={heroRef} className="flex w-full flex-col items-center gap-10">
					<div className="flex w-full flex-col items-center gap-6">
						{/* Avatar group */}
						<div className="flex items-center pr-2">
							<div className="-mr-2 size-[30px] overflow-hidden rounded-full outline outline-1 outline-white">
								<Image
									src="/images/avatar-jesse.jpg"
									alt="Jesse"
									width={30}
									height={30}
									className="size-full object-cover"
								/>
							</div>
							<div className="relative z-[1] size-[30px] overflow-hidden rounded-full outline outline-1 outline-white">
								<Image
									src="/images/avatar-friso.jpg"
									alt="Friso"
									width={30}
									height={30}
									className="size-full object-cover"
								/>
							</div>
						</div>

						{/* Title */}
						<div className="flex w-full flex-col items-center">
							<h1 className="flex flex-wrap items-center justify-center gap-1.5 font-display text-center text-[60px] font-bold leading-[60px] tracking-[-1.8px] text-[#090a0c]">
								<span>The Ultra</span>
								<span className="relative top-3 inline-flex h-12 w-[55px] items-end">
									<svg
										width="55"
										height="45"
										viewBox="0 0 55 45"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										aria-hidden="true"
									>
										<g filter="url(#filter0_dddd_2006_36538)">
											<rect
												x="4"
												width="46.5"
												height="31"
												rx="3.1"
												fill="white"
											/>
											<rect
												x="4"
												width="46.5"
												height="31"
												rx="3.1"
												fill="url(#paint0_linear_2006_36538)"
												fillOpacity="0.88"
											/>
											<path
												d="M13.1063 25.7684C13.1063 27.0525 12.0654 28.0934 10.7813 28.0934H9.23126C7.94734 28.0934 6.90625 27.0525 6.90625 25.7684C6.90625 24.4842 7.94734 23.4434 9.23126 23.4434H10.7813C12.0654 23.4434 13.1063 24.4844 13.1063 25.7684Z"
												fill="url(#paint1_linear_2006_36538)"
											/>
											<path
												d="M19.3062 24.6059C19.3062 25.2478 18.7856 25.7684 18.1437 25.7684H15.4312C14.1473 25.7684 13.1062 24.7275 13.1062 23.4434H18.1437C18.7856 23.4434 19.3062 23.9639 19.3062 24.6059Z"
												fill="url(#paint2_linear_2006_36538)"
											/>
											<path
												d="M16.9812 26.9309C16.9812 27.5728 16.4606 28.0934 15.8187 28.0934H13.1062C13.1062 26.8095 14.1473 25.7684 15.4312 25.7684H15.8187C16.1377 25.7684 16.4263 25.8968 16.6366 26.1047C16.8492 26.3155 16.9812 26.608 16.9812 26.9309Z"
												fill="url(#paint3_linear_2006_36538)"
											/>
										</g>
										<defs>
											<filter
												id="filter0_dddd_2006_36538"
												x="0"
												y="0"
												width="54.5"
												height="45"
												filterUnits="userSpaceOnUse"
												colorInterpolationFilters="sRGB"
											>
												<feFlood floodOpacity="0" result="BackgroundImageFix" />
												<feColorMatrix
													in="SourceAlpha"
													type="matrix"
													values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
													result="hardAlpha"
												/>
												<feOffset dy="1" />
												<feGaussianBlur stdDeviation="0.5" />
												<feColorMatrix
													type="matrix"
													values="0 0 0 0 0.0352941 0 0 0 0 0.0392157 0 0 0 0 0.0470588 0 0 0 0.06 0"
												/>
												<feBlend
													mode="normal"
													in2="BackgroundImageFix"
													result="effect1_dropShadow_2006_36538"
												/>
												<feColorMatrix
													in="SourceAlpha"
													type="matrix"
													values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
													result="hardAlpha"
												/>
												<feOffset dy="2" />
												<feGaussianBlur stdDeviation="1" />
												<feColorMatrix
													type="matrix"
													values="0 0 0 0 0.0352941 0 0 0 0 0.0392157 0 0 0 0 0.0470588 0 0 0 0.05 0"
												/>
												<feBlend
													mode="normal"
													in2="effect1_dropShadow_2006_36538"
													result="effect2_dropShadow_2006_36538"
												/>
												<feColorMatrix
													in="SourceAlpha"
													type="matrix"
													values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
													result="hardAlpha"
												/>
												<feOffset dy="6" />
												<feGaussianBlur stdDeviation="1.5" />
												<feColorMatrix
													type="matrix"
													values="0 0 0 0 0.0352941 0 0 0 0 0.0392157 0 0 0 0 0.0470588 0 0 0 0.03 0"
												/>
												<feBlend
													mode="normal"
													in2="effect2_dropShadow_2006_36538"
													result="effect3_dropShadow_2006_36538"
												/>
												<feColorMatrix
													in="SourceAlpha"
													type="matrix"
													values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
													result="hardAlpha"
												/>
												<feOffset dy="10" />
												<feGaussianBlur stdDeviation="2" />
												<feColorMatrix
													type="matrix"
													values="0 0 0 0 0.0352941 0 0 0 0 0.0392157 0 0 0 0 0.0470588 0 0 0 0.01 0"
												/>
												<feBlend
													mode="normal"
													in2="effect3_dropShadow_2006_36538"
													result="effect4_dropShadow_2006_36538"
												/>
												<feBlend
													mode="normal"
													in="SourceGraphic"
													in2="effect4_dropShadow_2006_36538"
													result="shape"
												/>
											</filter>
											<linearGradient
												id="paint0_linear_2006_36538"
												x1="27.25"
												y1="0"
												x2="27.25"
												y2="31"
												gradientUnits="userSpaceOnUse"
											>
												<stop stopColor="#090A0C" stopOpacity="0.8" />
												<stop offset="1" stopColor="#090A0C" />
											</linearGradient>
											<linearGradient
												id="paint1_linear_2006_36538"
												x1="13.1062"
												y1="23.4434"
												x2="13.1062"
												y2="28.0934"
												gradientUnits="userSpaceOnUse"
											>
												<stop stopColor="#C7C7C7" />
												<stop
													offset="1"
													stopColor="#C7C7C7"
													stopOpacity="0.88"
												/>
											</linearGradient>
											<linearGradient
												id="paint2_linear_2006_36538"
												x1="13.1062"
												y1="23.4434"
												x2="13.1062"
												y2="28.0934"
												gradientUnits="userSpaceOnUse"
											>
												<stop stopColor="#C7C7C7" />
												<stop
													offset="1"
													stopColor="#C7C7C7"
													stopOpacity="0.88"
												/>
											</linearGradient>
											<linearGradient
												id="paint3_linear_2006_36538"
												x1="13.1062"
												y1="23.4434"
												x2="13.1062"
												y2="28.0934"
												gradientUnits="userSpaceOnUse"
											>
												<stop stopColor="#C7C7C7" />
												<stop
													offset="1"
													stopColor="#C7C7C7"
													stopOpacity="0.88"
												/>
											</linearGradient>
										</defs>
									</svg>
								</span>
								<span>Plan</span>
							</h1>
						</div>

						{/* Subtitle */}
						<p className="font-display w-full text-center text-[18px] leading-7 tracking-[0.36px] text-[rgba(9,10,12,0.64)]">
							Your embedded design and dev team. Led by us.
						</p>
					</div>

					{/* CTAs */}
					<div className="flex gap-3">
						<button
							type="button"
							onClick={() => setIsWorkModalOpen(true)}
							className="rounded-[10px] border border-[rgba(9,10,12,0.12)] bg-white px-4 py-2 shadow-[0px_1px_2px_0px_rgba(9,10,12,0.02)] transition hover:bg-[rgba(9,10,12,0.04)]"
						>
							<span className="font-display text-base font-medium leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.88)]">
								See work
							</span>
						</button>
						<button
							type="button"
							onClick={() => {
								document
									.getElementById("book-call")
									?.scrollIntoView({ behavior: "smooth" });
							}}
							className="rounded-[10px] bg-[rgba(9,10,12,0.88)] px-4 py-2 shadow-[0px_1px_2px_0px_rgba(9,10,12,0.02)] transition hover:bg-[rgba(9,10,12,0.8)]"
						>
							<span className="font-display text-base font-medium leading-6 tracking-[0.32px] text-white">
								Book call
							</span>
						</button>
					</div>
				</div>

				{/* Body */}
				<div ref={bodyRef} className="flex w-full flex-col items-center gap-20">
					<PlanDivider />

					{/* Intro text */}
					<div className="flex w-full flex-col">
						<div className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
							<p>
								Everything Marathon has, but we lead. We don&apos;t just take
								priorities, we help set them. Senior design direction and
								strategy, not just execution. In your standups, your roadmap,
								your product decisions.
							</p>
							<p className="mt-6">
								This is design leadership without the hire. A larger dedicated
								team. multiple designers, developers, and a PM. embedded in your
								company and taking ownership of the work.
							</p>
						</div>
					</div>

					<PlanDivider />

					{/* What you get */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							What you get
						</h2>
						<div className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
							<p>
								A larger dedicated team: 2+ designers, developers, and a PM who
								knows your product inside out. Brand, product, growth, design
								systems, frontend and backend. all covered. One team, one
								relationship. Full strategy included.
							</p>
							<p className="mt-6">
								No juggling vendors. No context lost between handoffs. We lead
								design direction. you get senior creative leadership embedded in
								your team without the overhead of a full-time hire.
							</p>
						</div>
					</section>

					<PlanDivider />

					{/* Best for */}
					<section className="relative flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							Best for
						</h2>
						<div className="flex flex-col gap-3">
							<div className="flex items-start gap-2 font-display text-base leading-6 tracking-[0.32px]">
								<span className="text-[rgba(9,10,12,0.32)]">→</span>
								<span className="text-[rgba(9,10,12,0.64)]">
									Funded startups that need design leadership without the hire
								</span>
							</div>
							<div className="flex items-start gap-2 font-display text-base leading-6 tracking-[0.32px]">
								<span className="text-[rgba(9,10,12,0.32)]">→</span>
								<span className="text-[rgba(9,10,12,0.64)]">
									Teams where design decisions shape the product roadmap
								</span>
							</div>
							<div className="flex items-start gap-2 font-display text-base leading-6 tracking-[0.32px]">
								<span className="text-[rgba(9,10,12,0.32)]">→</span>
								<span className="text-[rgba(9,10,12,0.64)]">
									Companies scaling fast that need a full creative team embedded
								</span>
							</div>
							<div className="flex items-start gap-2 font-display text-base leading-6 tracking-[0.32px]">
								<span className="text-[rgba(9,10,12,0.32)]">→</span>
								<span className="text-[rgba(9,10,12,0.64)]">
									Products where strategy and execution need to be the same team
								</span>
							</div>
						</div>
					</section>

					<PlanDivider />

					{/* What a week looks like */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							What a week looks like
						</h2>
						<div className="flex flex-col gap-4">
							{/* Mon */}
							<div className="flex flex-col gap-2.5 border-l border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<div className="flex flex-col gap-1">
									<p className="font-display text-base font-semibold leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.88)]">
										Mon
									</p>
									<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
										We&apos;re in your standup, aligning on what matters this
										week.
									</p>
								</div>
								{/* Calendar card */}
								<div className="flex max-w-[400px] gap-2 rounded-[10px] bg-[rgba(16,185,129,0.08)] p-2.5">
									<div className="w-0.5 shrink-0 self-stretch rounded-sm bg-[#10b981]" />
									<div className="flex flex-1 flex-col">
										<p className="font-display text-sm font-semibold leading-5 text-[#10b981]">
											Weekly Alignment &lt;&gt; Outpace
										</p>
										<p className="font-display text-sm leading-5 text-[rgba(16,185,129,0.72)]">
											10:15 - 10:45 AM
										</p>
									</div>
									<div className="shrink-0 self-start rounded-full bg-[rgba(16,185,129,0.08)] px-2 py-1">
										<p className="font-display text-xs font-semibold leading-4 text-[#10b981]">
											30min
										</p>
									</div>
								</div>
							</div>

							{/* Tue - Thu */}
							<div className="relative border-l border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<div className="flex flex-col gap-1">
									<p className="font-display text-base font-semibold leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.88)]">
										Tue - Thu
									</p>
									<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
										Heads-down{" "}
										<span className="relative">
											<AnimatedSelection />
											{/* Decorative avatar chips */}
											<span
												className="pointer-events-none absolute -top-8 left-1/2 flex -translate-x-1/2 items-center select-none"
												aria-hidden="true"
											>
												<span className="relative z-30 size-[19px] overflow-hidden rounded-[5px] outline outline-1 outline-[#ec4899]">
													<Image
														src="/images/avatar-jesse.jpg"
														alt=""
														width={19}
														height={19}
														className="size-full object-cover"
													/>
												</span>
												<span className="relative z-20 -ml-2 size-[19px] overflow-hidden rounded-[5px] outline outline-1 outline-[#ec4899]">
													<Image
														src="/images/avatar-friso.jpg"
														alt=""
														width={19}
														height={19}
														className="size-full object-cover"
													/>
												</span>
												<span className="relative z-10 -ml-2 size-[19px] overflow-hidden rounded-[5px] outline outline-1 outline-[#ec4899]">
													<Image
														src="/images/avatar-jesse.jpg"
														alt=""
														width={19}
														height={19}
														className="size-full object-cover"
													/>
												</span>
											</span>
										</span>
										, decisions made in{" "}
										<span className="relative before:absolute before:-inset-x-0.5 before:-inset-y-px before:rounded-sm before:bg-[rgba(14,165,233,0.2)]">
											<span className="relative text-[rgba(14,165,233,0.88)]">
												real-time
											</span>
										</span>
										, strategy discussions as they come up.
									</p>
								</div>
								{/* Decorative cursor + Friso tag */}
								<motion.div
									className="pointer-events-none absolute -bottom-5 right-0 flex flex-col items-start select-none"
									aria-hidden="true"
									animate={{
										x: [0, 5, -2, 7, 0],
										y: [0, -8, -14, -5, 0],
									}}
									transition={{
										duration: 5,
										ease: "easeInOut",
										repeat: Infinity,
										times: [0, 0.25, 0.55, 0.8, 1],
									}}
								>
									<svg
										width="27"
										height="27"
										viewBox="0 0 27 27"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g filter="url(#filter0_d_2006_36398)">
											<path
												d="M9.25859 17.1184L5.85677 4.42285C5.63452 3.5934 6.49218 2.88804 7.26312 3.26624L19.8303 9.43128C20.7285 9.8719 20.4947 11.2125 19.5003 11.323L14.4622 11.8828C14.1319 11.9194 13.8414 12.1178 13.6871 12.4121L11.11 17.3241C10.687 18.1305 9.49426 17.9979 9.25859 17.1184Z"
												fill="#090A0C"
											/>
											<path
												d="M9.25859 17.1184L5.85677 4.42285C5.63452 3.5934 6.49218 2.88804 7.26312 3.26624L19.8303 9.43128C20.7285 9.8719 20.4947 11.2125 19.5003 11.323L14.4622 11.8828C14.1319 11.9194 13.8414 12.1178 13.6871 12.4121L11.11 17.3241C10.687 18.1305 9.49426 17.9979 9.25859 17.1184Z"
												stroke="white"
											/>
										</g>
										<defs>
											<filter
												id="filter0_d_2006_36398"
												x="-0.000827312"
												y="-0.000413656"
												width="26.2126"
												height="26.3415"
												filterUnits="userSpaceOnUse"
												colorInterpolationFilters="sRGB"
											>
												<feFlood floodOpacity="0" result="BackgroundImageFix" />
												<feColorMatrix
													in="SourceAlpha"
													type="matrix"
													values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
													result="hardAlpha"
												/>
												<feOffset dy="2.66057" />
												<feGaussianBlur stdDeviation="2.66057" />
												<feComposite in2="hardAlpha" operator="out" />
												<feColorMatrix
													type="matrix"
													values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"
												/>
												<feBlend
													mode="normal"
													in2="BackgroundImageFix"
													result="effect1_dropShadow_2006_36398"
												/>
												<feBlend
													mode="normal"
													in="SourceGraphic"
													in2="effect1_dropShadow_2006_36398"
													result="shape"
												/>
											</filter>
										</defs>
									</svg>
									<div className="ml-3 flex items-center gap-1.5 rounded-[9px] border border-[rgba(0,0,0,0.12)] bg-white py-1 pl-1 pr-1.5 opacity-90 shadow-[0px_2px_16px_0px_rgba(0,0,0,0.08)]">
										<div className="size-[18px] overflow-hidden rounded-[5px]">
											<Image
												src="/images/avatar-friso.jpg"
												alt=""
												width={18}
												height={18}
												className="size-full object-cover"
											/>
										</div>
										<p className="font-display text-sm font-semibold leading-[14px] text-[rgba(9,10,12,0.72)]">
											Friso
										</p>
									</div>
								</motion.div>
							</div>

							{/* Fri */}
							<div className="flex flex-col gap-2.5 border-l border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<div className="flex flex-col gap-1">
									<p className="font-display text-base font-semibold leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.88)]">
										Fri
									</p>
									<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
										Deliverables shipped, next week scoped.
									</p>
								</div>
								{/* Slack message mock */}
								<div className="flex max-w-[400px] gap-2 rounded-xl bg-[rgba(9,10,12,0.04)] p-2.5">
									<div className="size-10 shrink-0 overflow-hidden rounded-lg">
										<Image
											src="/images/avatar-friso.jpg"
											alt=""
											width={40}
											height={40}
											className="size-full object-cover"
										/>
									</div>
									<div className="flex flex-1 flex-col gap-2">
										<div className="flex flex-col gap-0.5">
											<div className="flex items-baseline gap-1">
												<p className="font-display text-sm font-semibold leading-5 tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
													You
												</p>
												<p className="font-display text-xs leading-4 tracking-[0.24px] text-[rgba(9,10,12,0.56)]">
													9:41 PM
												</p>
											</div>
											<p className="font-display text-sm leading-5 tracking-[0.28px] text-[rgba(9,10,12,0.64)]">
												This is fire guys, y&apos;all are cooking.
											</p>
										</div>
										{/* Emoji reactions */}
										<Reactions />
									</div>
								</div>
							</div>
						</div>
					</section>

					<PlanDivider />

					{/* Testimonials */}
					<div className="flex w-full flex-col gap-4">
						{[
							{
								quote:
									"It\u2019s a delight to work with this team. Outpace knocked it out of the park with our product and milestones!",
								name: "Frank Roskam",
								role: "Co-Founder, Celebratix",
								image: "/assets/testimonials/frank.jpg",
							},
							{
								quote:
									"You guys are legends, can\u2019t thank you enough. Getting such good feedback on the app from the community.",
								name: "Steve McFadyen",
								role: "Founder, POAP Global",
								image: "/assets/testimonials/steve.jpg",
							},
							{
								quote:
									"Amazing designers that live and breathe web3. Every design choice they made showed deep strategic thinking and a passion for our mission.",
								name: "Pedro Galv\u00e1n",
								role: "Head of Product, AITA Protocol",
								image: "/assets/testimonials/pedro.jpg",
							},
						].map((t) => (
							<div
								key={t.name}
								className="flex w-full border-l border-[rgba(0,0,0,0.08)] py-4 pl-8"
							>
								<div className="flex flex-col gap-4">
									<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
										&ldquo;{t.quote}&rdquo;
									</p>
									<div className="flex items-center gap-3">
										<div className="size-10 overflow-hidden rounded-lg">
											<Image
												src={t.image}
												alt={t.name}
												width={40}
												height={40}
												className="size-full object-cover"
											/>
										</div>
										<div className="flex flex-col gap-0.5">
											<p className="font-display text-sm font-semibold leading-5 tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
												{t.name}
											</p>
											<p className="font-display text-sm leading-5 tracking-[0.28px] text-[rgba(9,10,12,0.48)]">
												{t.role}
											</p>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>

					<PlanDivider />

					{/* Join The Ultra */}
					<section
						ref={formRef}
						id="book-call"
						className="flex w-full flex-col gap-10"
					>
						<div className="flex flex-col gap-6">
							<div className="flex items-center justify-between">
								<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
									Claim your spot
								</h2>
								<div className="rounded-lg bg-[rgba(245,158,11,0.12)] px-2 py-1">
									<p className="font-display text-center text-sm font-medium leading-5 text-[rgba(245,158,11,0.88)]">
										1 / 2 available
									</p>
								</div>
							</div>
							<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
								We take on 2 Ultra clients at a time. One spot just opened up.
								Book a call and we&apos;ll see if it&apos;s the right fit.
							</p>
						</div>

						{/* Checklist */}
						<div className="flex border-l border-[rgba(0,0,0,0.08)] py-4 pl-8">
							<div className="flex flex-col gap-3">
								{[
									"Senior creative leadership without the full-time hire",
									"Brand, product, growth, design systems, and full-stack dev. all covered",
									"One team, one relationship. No vendor juggling",
								].map((text) => (
									<div key={text} className="flex items-start gap-3">
										<div className="flex h-6 shrink-0 items-center">
											<svg
												width="18"
												height="18"
												viewBox="0 0 18 18"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												aria-hidden="true"
											>
												<path
													fillRule="evenodd"
													clipRule="evenodd"
													d="M9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5ZM11.6854 7.4812C11.8821 7.24076 11.8466 6.88637 11.6062 6.68965C11.3658 6.49293 11.0114 6.52837 10.8146 6.7688L7.83324 10.4127L6.77275 9.35225C6.55308 9.13258 6.19692 9.13258 5.97725 9.35225C5.75758 9.57192 5.75758 9.92808 5.97725 10.1477L7.47725 11.6477C7.58963 11.7601 7.74429 11.8197 7.90302 11.8118C8.06175 11.8039 8.20972 11.7292 8.31035 11.6062L11.6854 7.4812Z"
													fill="rgba(9,10,12,0.32)"
												/>
											</svg>
										</div>
										<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.72)]">
											{text}
										</p>
									</div>
								))}
							</div>
						</div>

						{/* Book call sub-section */}
						<div className="flex flex-col gap-6">
							<p className="font-display text-base font-semibold leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.88)]">
								Book call
							</p>

							<BookingCalendar plan="ultra" />
						</div>
					</section>

					<PlanDivider />

					{/* Logos */}
					<IntroLogos title="Trusted by industry-leaders" gap="lg" />

					<PlanDivider />

					{/* Questions */}
					<section className="flex w-full flex-col gap-8">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							Questions
						</h2>
						<div className="flex flex-col gap-8">
							{[
								{
									q: "How is Ultra different from Marathon?",
									a: "Marathon is execution. You set priorities, we ship. Ultra is leadership. We help set the priorities, lead design direction, and operate as part of your team. Bigger dedicated team, full strategy included, and we\u2019re in your standups and roadmap, not just your Slack.",
								},
								{
									q: "What does the team look like?",
									a: "2+ designers, developers, and a dedicated PM who knows your product inside out. Same people every week. No rotations, no handoffs.",
								},
								{
									q: "What does $15k/month actually cover?",
									a: "Brand, product design, growth, design systems, and full-stack development. One team, one relationship. No separate invoices for dev or strategy.",
								},
								{
									q: "Why only 2 spots?",
									a: "Because we embed deeply. We\u2019re in your standups, your roadmap, your product decisions. That level of involvement doesn\u2019t scale past two clients without quality dropping. We\u2019d rather do it right.",
								},
								{
									q: "What if I need to pause or stop?",
									a: "We\u2019ll wrap up what\u2019s in progress and hand everything over clean. All source files, design systems, and documentation are yours.",
								},
								{
									q: "How fast can you start?",
									a: "Once we align on goals and scope, we typically kick off within a week. Your PM sets up the workflow and we\u2019re shipping from day one.",
								},
								{
									q: "Can I start with Marathon and upgrade to Ultra?",
									a: "Absolutely. A lot of teams start with Marathon to test the fit, then move to Ultra when they want us leading, not just executing.",
								},
								{
									q: "Do I need to manage you?",
									a: "That\u2019s the whole point of Ultra. We don\u2019t wait for a brief. We help write it. You get senior creative leadership embedded in your team without the overhead of managing a vendor.",
								},
							].map((item) => (
								<div key={item.q} className="flex flex-col gap-3">
									<p className="font-display text-base font-semibold leading-6 tracking-[0.16px] text-[rgba(9,10,12,0.88)]">
										{item.q}
									</p>
									<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
										{item.a}
									</p>
								</div>
							))}
						</div>
					</section>

					<PlanDivider />

					{/* Footer */}
					<div
						ref={footerRef}
						className="flex flex-col items-center gap-0.5 text-center"
					>
						<p className="font-display text-base font-medium leading-6 text-[rgba(9,10,12,0.56)]">
							Forever forward,
						</p>
						<p className="font-display text-base font-semibold leading-6 tracking-[-0.16px] text-[#ff4f1a]">
							Outpace Studios™
						</p>
					</div>
				</div>
			</div>
			<UltraWorkModal
				isOpen={isWorkModalOpen}
				onClose={() => setIsWorkModalOpen(false)}
			/>
		</div>
	);
}
